import { useState, useEffect } from 'react';
import { DeviceRegistrationService } from '@/services/deviceRegistrationService';
import { toast } from '@/hooks/use-toast';

export const useNotificationPermissions = (userId?: string) => {
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');
  const [deviceRegistered, setDeviceRegistered] = useState(false);
  const [vapidKeyUpdateDetected, setVapidKeyUpdateDetected] = useState(false);

  useEffect(() => {
    checkNotificationPermission();
    checkDeviceRegistration();
    checkForVapidKeyUpdate();
  }, []);

  const checkForVapidKeyUpdate = async () => {
    // Check if we need to re-register due to VAPID key update
    // This can be detected by checking if existing subscription still works
    try {
      if ('serviceWorker' in navigator && userId) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.pushManager) {
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            // Test if subscription is still valid by checking device registration
            const deviceInfo = await DeviceRegistrationService.getCurrentDeviceInfo();
            if (!deviceInfo.token) {
              setVapidKeyUpdateDetected(true);
            }
          }
        }
      }
    } catch (error) {
      console.log('VAPID key update check completed');
    }
  };

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }
  };

  const checkDeviceRegistration = async () => {
    try {
      const deviceInfo = await DeviceRegistrationService.getCurrentDeviceInfo();
      setDeviceRegistered(!!deviceInfo.token);
    } catch (error) {
      console.error('Error checking device registration:', error);
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      console.log('🔔 Current notification permission:', Notification.permission);
      
      // Clear any potential blocks and request fresh permission
      const permission = await Notification.requestPermission();
      console.log('🔔 New notification permission:', permission);
      setPermissionStatus(permission);
      
      if (permission === 'granted' && userId) {
        try {
          console.log('✅ Permission granted, registering device for user:', userId);
          const result = await DeviceRegistrationService.registerCurrentDevice(userId);
          console.log('📱 Device registration result:', result);
          
          if (result.success) {
            setDeviceRegistered(true);
            setVapidKeyUpdateDetected(false);
            toast({
              title: "Notifications enabled",
              description: "Your device has been registered for push notifications with updated security keys.",
            });
          } else {
            throw new Error(result.error || 'Registration failed');
          }
        } catch (error) {
          console.error('❌ Device registration failed:', error);
          toast({
            title: "Registration failed",
            description: error instanceof Error ? error.message : "Failed to register device for notifications.",
            variant: "destructive",
          });
        }
      } else if (permission === 'denied') {
        toast({
          title: "Notifications blocked",
          description: "Please enable notifications in your browser settings for this site.",
          variant: "destructive",
        });
      }
    }
  };

  return {
    permissionStatus,
    deviceRegistered,
    vapidKeyUpdateDetected,
    requestNotificationPermission,
  };
};